/**
 * quantumFoamPipeline — Planck-scale spacetime foam.
 *
 * At the Planck scale (10^-35 m), spacetime is believed to be
 * turbulent and "foamy" — quantum fluctuations create a
 * constantly bubbling structure. We visualize this by overlaying
 * many small "quantum fluctuation" cells that pulse in and out
 * of existence.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface QuantumFoamPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * valueNoise(p);
    p = p * 2.0 + 7.1;
    a *= 0.5;
  }
  return v;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  float r = length(p);
  vec3 bg = mix(
    vec3(0.02, 0.04, 0.10),
    vec3(0.005, 0.005, 0.02),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Layers of small "cells" that pulse in/out.
  // Each layer has a different scale.
  for (int layer = 0; layer < 4; layer++) {
    float fl = float(layer);
    float scale = 8.0 * pow(2.0, fl);
    float speed = 1.5 * pow(1.5, fl);
    vec2 q = p * scale;
    vec2 cell = floor(q);
    vec2 local = fract(q) - 0.5;
    // Random birth time per cell.
    float birthTime = hash21(cell + vec2(fl * 7.0, 13.0)) * 6.28;
    float birthPhase = fract(uTime * speed * 0.3 + birthTime - fl * 0.5);
    // The cell is "alive" for a fraction of the cycle.
    float alive = step(0.3 + 0.5 * high, birthPhase) * step(birthPhase, 0.8);
    float d = length(local);
    // The cell grows and shrinks.
    float radius = 0.3 * (1.0 - abs(birthPhase - 0.5) * 2.0);
    float cellMask = 1.0 - smoothstep(radius - 0.05, radius + 0.05, d);
    // Color: warm tint per layer.
    vec3 cellColor = vec3(0.6, 0.4, 0.95);
    if (mod(fl, 2.0) < 0.5) cellColor = vec3(0.4, 0.85, 0.95);
    if (mod(fl, 3.0) < 0.5) cellColor = vec3(0.95, 0.55, 0.30);
    col += cellColor * cellMask * alive * (0.5 + 0.5 * mid);
  }

  // The "foam" — large-scale fbm that gives the bubbles a
  // collective texture.
  float foam = fbm(p * 6.0 + vec2(uTime * 0.1, 0.0));
  col += vec3(0.10, 0.06, 0.15) * foam * 0.3;

  // Particle "fluctuations" — small bright dots that twinkle.
  for (int i = 0; i < 15; i++) {
    float fi = float(i);
    vec2 fp = vec2(
      sin(fi * 1.7 + 0.3) * 1.0,
      cos(fi * 2.1 + 1.7) * 0.8
    );
    float twinkle = step(0.7, hash21(vec2(fi, floor(uTime * 3.0))));
    float d = length(p - fp);
    col += vec3(0.9, 0.95, 1.0) * exp(-d * 200.0) * twinkle * 0.5;
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeQuantumFoamBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeQuantumFoamRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createQuantumFoamPipeline(gl: WebGL2RenderingContext): QuantumFoamPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawQuantumFoam(
  gl: WebGL2RenderingContext,
  pipe: QuantumFoamPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
